import { useCallback } from 'react';

import { GOOGLE_RECAPTCHA_SITE_KEY, GOOGLE_RECAPTCHA_SITE_KEY_OLD } from '~/constants';
import { FeatureFlag } from '~/lib/flags/flags';
import { useFeatureFlag } from '~/lib/flags/hooks';
import { log } from '~/util';

type SuccessCallback = (reCaptchaToken: string) => void;
type ErrorCallback = (error: Error) => void;

export type HandleReCaptcha = (onSuccess: SuccessCallback, onError: ErrorCallback) => void;

export const RECAPTCHA_CONTAINER_ID = 'google-recaptcha-container';
const RECAPTCHA_ACTION = 'submit';

type ReCaptchaRenderParameters = {
  sitekey: string;
  action: string;
  badge: 'inline' | 'bottomleft' | 'bottomright';
  size: 'invisible';
};

type ReCaptchaEnterprise = {
  ready: (callback: () => void) => void;
  render: (container: string | HTMLElement, parameters: ReCaptchaRenderParameters) => number;
  execute: (widgetId: number) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: ReCaptchaEnterprise;
    };
  }
}

let widgetId: number | undefined;

const getReCaptchaEnterprise = async (): Promise<ReCaptchaEnterprise> => {
  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise) {
    throw new Error('reCaptcha not loaded');
  }

  await new Promise<void>((resolve) => enterprise.ready(resolve));

  return enterprise;
};

const getReCaptchaWidgetId = async (siteKey: string): Promise<number> => {
  if (widgetId !== undefined) {
    return widgetId;
  }

  if (!siteKey) {
    throw new Error('reCaptcha site key not configured');
  }

  const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    throw new Error('reCaptcha container not found');
  }

  const enterprise = await getReCaptchaEnterprise();

  widgetId = enterprise.render(container, {
    sitekey: siteKey,
    action: RECAPTCHA_ACTION,
    badge: 'inline',
    size: 'invisible',
  });

  return widgetId;
};

const executeReCaptcha = async (siteKey: string): Promise<string> => {
  const id = await getReCaptchaWidgetId(siteKey);
  const enterprise = await getReCaptchaEnterprise();

  return enterprise.execute(id);
};

const getReCaptchaError = (error: unknown): Error => {
  const message = error instanceof Error ? error.message : error;

  if (message === 'Challenge cancelled by user.') {
    return new Error('reCAPTCHA challenge not completed');
  }

  return new Error('reCAPTCHA error');
};

const useReCaptchaSiteKey = () => {
  const cutover = useFeatureFlag(FeatureFlag.CutoverRecaptcha, { default: false });

  return cutover ? GOOGLE_RECAPTCHA_SITE_KEY : GOOGLE_RECAPTCHA_SITE_KEY_OLD;
};

export const reCaptchaCheck = (): { handleReCaptcha: HandleReCaptcha } => {
  const siteKey = useReCaptchaSiteKey();

  const handleReCaptcha = useCallback(
    (onSuccess: SuccessCallback, onError: ErrorCallback) => {
      executeReCaptcha(siteKey)
        .then(onSuccess)
        .catch((error) => {
          onError(getReCaptchaError(error));

          log({ name: 'reCaptcha error', type: 'error', context: { error } });
        });
    },
    [siteKey]
  );

  return { handleReCaptcha };
};

export const useReCaptcha = (): (() => Promise<string>) => {
  const siteKey = useReCaptchaSiteKey();

  return useCallback(() => {
    return executeReCaptcha(siteKey).catch((error) => {
      log({ name: 'reCaptcha error', type: 'error', context: { error } });
      throw getReCaptchaError(error);
    });
  }, [siteKey]);
};
